/**
 * Get the API base URL based on the current hostname
 * @returns {string} The base URL for API requests
 */
export const getApiBaseUrl = () => {
  return (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') 
    ? 'http://localhost:8082' 
    : `${window.location.origin}/api`;
};

/**
 * Base URL for the vibecoder iframe preview route. In production Apache
 * proxies /preview/ straight to the backend (alongside the /api/ proxy
 * rule), so the public origin is used WITHOUT the /api prefix. In dev the
 * SPA on :3000 talks to the backend on :8082 directly.
 *
 * Used together with the path returned by POST /api/websites/preview-signature
 * to build the iframe src URL — see fetchPreviewUrl below.
 */
export const getPreviewBaseUrl = () => {
  return (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:8082'
    : window.location.origin;
};

/**
 * WebSocket base URL for speech transcription. In production Apache proxies
 * /ws/ to the backend (not under /api/). In dev the SPA on :3000 talks to
 * the backend on :8082 directly.
 */
export const getWsBaseUrl = () => {
  const isLocal =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  if (isLocal) {
    return 'ws://localhost:8082';
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
};

/**
 * Decode a JWT payload without verification (for checking expiry client-side).
 * Returns null if the token is malformed.
 */
export const decodeJwtPayload = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

/**
 * Check whether the stored JWT is expired or will expire within `bufferSeconds`.
 * Defaults to a 5-minute buffer so we can refresh proactively.
 */
export const isTokenExpiringSoon = (bufferSeconds = 300) => {
  const token = localStorage.getItem('access_token');
  if (!token) return true;

  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return true;

  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp - nowSeconds < bufferSeconds;
};

// ------------------------------------------------------------------
// Token-refresh callback – set by AuthContext so authenticatedFetch
// can trigger a silent refresh without importing MSAL directly.
// ------------------------------------------------------------------
let _refreshTokenCallback = null;

export const setTokenRefreshCallback = (cb) => {
  _refreshTokenCallback = cb;
};

/**
 * Attempt a silent token refresh via the callback registered by AuthContext.
 * Returns the new token string on success, or null on failure.
 */
const attemptSilentRefresh = async () => {
  if (typeof _refreshTokenCallback === 'function') {
    try {
      return await _refreshTokenCallback();
    } catch (err) {
      console.warn('Silent token refresh failed:', err);
    }
  }
  return null;
};

// ------------------------------------------------------------------
// Interactive re-auth fallback – set by AuthContext. MSAL's silent
// acquireTokenSilent falls back to a hidden-iframe SSO request when it
// can't satisfy a refresh from cache; Chrome's third-party-cookie
// blocking and Edge's Tracking Prevention intermittently break that
// iframe's access to the identity provider's session cookie, causing
// silent refresh to fail unpredictably in exactly those two browsers.
// A full top-level redirect (acquireTokenRedirect) sidesteps that
// restriction -- it's a first-party navigation, not a third-party
// iframe -- so it's tried here as a last resort before giving up and
// hard-redirecting to /login, which otherwise destroys whatever
// multi-step flow (e.g. project setup) was in progress with zero
// explanation. See setInteractiveReauthCallback's registration in
// AuthContext.js for what this actually calls.
// ------------------------------------------------------------------
let _interactiveReauthCallback = null;

export const setInteractiveReauthCallback = (cb) => {
  _interactiveReauthCallback = cb;
};

// Lets authenticatedFetch tell the user WHY the page is about to
// redirect, instead of it just happening with no explanation.
let _authNoticeCallback = null;

export const setAuthNoticeCallback = (cb) => {
  _authNoticeCallback = cb;
};

// acquireTokenRedirect navigates away immediately, so this is only ever
// retried once per window -- without a cooldown, a backend that keeps
// rejecting the freshly-reacquired token would redirect-loop the user
// through the identity provider forever.
const INTERACTIVE_REAUTH_COOLDOWN_KEY = 'kai_interactive_reauth_attempted_at';
const INTERACTIVE_REAUTH_COOLDOWN_MS = 60_000;

const attemptInteractiveReauth = async () => {
  if (typeof _interactiveReauthCallback !== 'function') return false;
  try {
    const lastAttempt = Number(sessionStorage.getItem(INTERACTIVE_REAUTH_COOLDOWN_KEY) || 0);
    if (Date.now() - lastAttempt < INTERACTIVE_REAUTH_COOLDOWN_MS) return false;
    sessionStorage.setItem(INTERACTIVE_REAUTH_COOLDOWN_KEY, String(Date.now()));
  } catch (e) {
    // sessionStorage unavailable (private mode, etc.) -- fall through and
    // let the redirect attempt happen anyway rather than blocking on this.
  }
  try {
    _authNoticeCallback?.('Your session needs to be refreshed. Reconnecting…');
    // Fired, deliberately NOT awaited: acquireTokenRedirect navigates the
    // page away, and whether its returned promise ever actually resolves
    // is browser/MSAL-version-dependent -- awaiting it here would race
    // that navigation. As soon as the call is dispatched without throwing
    // synchronously, treat it as "recovery under way" so the caller skips
    // the old hard-redirect-to-/login fallback below, which would only
    // cancel the in-flight navigation to the identity provider.
    _interactiveReauthCallback().catch((err) => {
      console.warn('Interactive re-authentication failed:', err);
    });
    return true;
  } catch (err) {
    console.warn('Interactive re-authentication failed to start:', err);
    return false;
  }
};

// Create authenticated fetch function
export const authenticatedFetch = async (url, options = {}) => {
  // Proactively refresh if the token is about to expire
  if (isTokenExpiringSoon()) {
    const refreshed = await attemptSilentRefresh();
    if (!refreshed) {
      // Could not refresh – fall through; the request will likely 401
      console.warn('Token expiring soon but silent refresh unavailable');
    }
  }

  let token = localStorage.getItem('access_token');
  
  if (!token) {
    throw new Error('No access token available');
  }
  
  const buildHeaders = (tkn) => {
    const hdrs = {
      'Authorization': `Bearer ${tkn}`,
      ...options.headers
    };
    // Only add Content-Type if not explicitly set to empty object (for FormData)
    if (options.headers !== undefined && Object.keys(options.headers).length === 0) {
      // Don't add Content-Type for FormData uploads
    } else if (!hdrs['Content-Type']) {
      hdrs['Content-Type'] = 'application/json';
    }
    return hdrs;
  };

  // `credentials: 'include'` is required so the kai_session HttpOnly cookie
  // round-trips on cross-origin dev requests (localhost:3000 -> localhost:8082).
  // In production the frontend and backend share an origin, so this is a no-op
  // there. Backend must respond with `Access-Control-Allow-Credentials: true`
  // and a specific (non-`*`) `Access-Control-Allow-Origin` for the response
  // to be accepted by the browser — see backend `.env` ALLOWED_ORIGINS.
  let response = await fetch(url, {
    credentials: 'include',
    ...options,
    headers: buildHeaders(token)
  });

  // On 401, try one silent refresh before giving up
  if (response.status === 401) {
    const newToken = await attemptSilentRefresh();
    if (newToken) {
      // Retry the original request with the fresh token
      response = await fetch(url, {
        credentials: 'include',
        ...options,
        headers: buildHeaders(newToken)
      });
    }

    // If still 401 after a silent refresh attempt, try an interactive
    // (redirect-based) re-auth before giving up -- see
    // attemptInteractiveReauth's own comment for why this exists. If that
    // doesn't recover (not registered, on cooldown, or itself failed),
    // fall back to the original behavior: drop the token and hard-redirect
    // to /login.
    if (response.status === 401) {
      const recovered = await attemptInteractiveReauth();
      if (!recovered) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      }
      throw new Error('Authentication required');
    }
  }
  
  return response;
};

/**
 * Mint a fresh signed preview URL for the vibecoder iframe.
 *
 * The backend returns a path like `/preview/websites/v/<hmac-sig>/<id>/index.html`.
 * We prepend the appropriate host (localhost:8082 in dev, current origin in
 * prod) and return the absolute URL. The signature has a finite TTL
 * (default 4h server-side), so the caller should re-mint whenever the
 * preview is (re)opened.
 *
 * Bearer-auth via authenticatedFetch — only logged-in KPF users can mint
 * a preview URL. The mint itself doesn't go through any iframe so the
 * usual cookie-in-sandboxed-iframe problems don't apply here.
 */
export const fetchPreviewUrl = async (projectId) => {
  if (!projectId) throw new Error('projectId is required');
  const res = await authenticatedFetch(`${getApiBaseUrl()}/websites/preview-signature`, {
    method: 'POST',
    body: JSON.stringify({ project_id: projectId }),
  });
  if (!res.ok) {
    throw new Error(`Failed to mint preview URL (status ${res.status})`);
  }
  const { url } = await res.json();
  if (!url || !url.startsWith('/preview/')) {
    throw new Error('Malformed preview URL from server');
  }
  return `${getPreviewBaseUrl()}${url}`;
};
